import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { tryExecuteAsync } from "@app/utils/tryer";
import { ComputeOrderResponse } from "@dtos/orders-dtos/compute-order-response";
import { CreateOrderRequest } from "@dtos/orders-dtos/create-order-request";
import { CreateOrderResponse } from "@dtos/orders-dtos/create-order-response";
import { OrderDetailDto } from "@dtos/orders-dtos/order-detailed-dto";
import { OrderForListDto } from "@dtos/orders-dtos/order-for-list-dto";
import { Storage } from "@ionic/storage-angular";
import { MessageBusService } from "@services/message-bus.service";
import { RequestHandlerService } from "@services/request-handler.service";
import { ValuesService } from "@services/values.service";
import { DateTime } from "luxon";
import hash from 'object-hash';
import { firstValueFrom } from "rxjs";
import { environment } from '@envs/environment';
import { constants } from "../shared/constants/variables";
import { DatetimeUtils } from "../shared/utils/datetime-utils";
import { LoggerService } from "./logger.service";

export type RoutingPhase = 'none' | 'stale' | 'terminal' | 'inFlight' | 'resume';

@Injectable({
    providedIn: 'root'
})
export class OrderService {
    private _baseUrl = environment.apiUrl + 'Orders/';
    private _http = inject(HttpClient);
    private _logger = inject(LoggerService);
    private _storage = inject(Storage);
    private readonly _req = inject(RequestHandlerService);
    private readonly _valuesService = inject(ValuesService);
    private readonly _messageBus = inject(MessageBusService);

    isCreatingOrder = signal(false);
    isComputingOrder = signal(false);
    isFetchingOrderDetail = signal(false);
    order = signal<CreateOrderResponse | undefined>(undefined);
    computation = signal<ComputeOrderResponse | undefined>(undefined);
    paymentMethod = signal<'PayMongo' | 'Cash' | undefined>(undefined);
    private readonly _routingPhase = signal<RoutingPhase>('none');
    readonly routingPhase = this._routingPhase.asReadonly();

    isAdvancedOrder = computed(() => {
        if (!this.order()) return false;
        const createdAt = DateTime.fromISO(this.order().createDate, {zone: DatetimeUtils.UTC_TIME_ZONE});
        const activeAt = DateTime.fromISO(this.order().activeDate, {zone: DatetimeUtils.UTC_TIME_ZONE});
        return Math.abs(createdAt.diff(activeAt, 'hours').hours) >= 1;
    });

    private _rehydrationResolve!: () => void;
    readonly rehydrationReady = new Promise<void>(resolve => { this._rehydrationResolve = resolve; });

    constructor() {
        this._initializeRehydration();

        // Deliberately not clearing on onPaymentAuthorized — auth means the order
        // started routing, not that it's done. The resume guard reads these signals.
        toObservable(this.order)
            .pipe(takeUntilDestroyed())
            .subscribe(async order => {
                if (order) {
                    const isAlreadyComputed = this.computation()?.id === order.id;
                    if (isAlreadyComputed) return;
                    this._logger.debug('OrderService -- order was created. computing order');
                    await this.computeOrder(order.id);
                } else {
                    this._logger.debug('OrderService -- order was cleared. setting computation to undefined');
                    this.computation.set(undefined);
                    return;
                }
            });

        effect(async () => {
            const computation = this.computation();
            if (!computation) return;

            const storedOrder = await this.getOrderFromLocalStorage();
            if (storedOrder && storedOrder.order.id === computation.id && !storedOrder.computation) {
                storedOrder.computation = computation;
                await this._storage.set(constants.storageKeys.ExistingOrderDetails, storedOrder);
            }
        });
    }

    async createOrder(dto: CreateOrderRequest): Promise<CreateOrderResponse | undefined> {
        this._logger.debug('OrderService -- creating order', dto);

        const storedOrder = await this.getOrderFromLocalStorageIfHashMatches(dto);
        if (storedOrder) {
            this._logger.debug('OrderService -- returning existing order from local storage', storedOrder);
            this.order.set(storedOrder.order);
            return this.order();
        }

        const response = await this._req.post<CreateOrderResponse | undefined>(
            this._baseUrl,
            dto,
            this.isCreatingOrder,
        );
        if (response.type === 'error') {
            this._logger.debug('OrderService -- Order creation failed, no order returned');
            this.order.set(undefined)
            return undefined;
        }

        const createdOrder = response.data;
        this._logger.debug('OrderService -- Order created', createdOrder);

        const orderDetailsForStorage: OrderDetailsForStorage = {
            order: createdOrder,
            hash: hash(dto),
        }

        this.order.set(createdOrder);
        await this._storage.set(constants.storageKeys.ExistingOrderDetails, orderDetailsForStorage);
        return this.order();
    }

    getOrdersHistory(page: number, pageSize: number) {
        return firstValueFrom(this._http.get<OrderForListDto[]>(this._baseUrl + `History?page=${page}&pageSize=${pageSize}`));
    }

    async getOrderDetails(statusSignal: WritableSignal<boolean>, orderId: number, accessKey?: string) {
        this._logger.debug('OrderService -- getting order details for orderId:', orderId, 'with accessKey:', accessKey);
        const url = accessKey ? this._baseUrl + orderId + '/' + accessKey : this._baseUrl + orderId;
        return tryExecuteAsync<OrderDetailDto>(firstValueFrom(this._http.get<OrderDetailDto>(url)), statusSignal);
    }

    async getOrderFromLocalStorageIfHashMatches(dto: CreateOrderRequest): Promise<OrderDetailsForStorage | undefined> {
        const storedOrder = await this.getOrderFromLocalStorage();
        if (!storedOrder) return undefined;

        this._logger.debug('OrderService -- found existing order in local storage', storedOrder);

        if (storedOrder.hash === hash(dto)) {
            this._logger.debug('OrderService -- existing order matches the new order, returning existing order');
            return storedOrder;
        }

        return undefined;
    }

    async getOrderFromLocalStorage(): Promise<OrderDetailsForStorage | undefined> {
        try {
            return await this._storage.get(constants.storageKeys.ExistingOrderDetails);
        } catch (ex) {
            this._logger.debug('OrderService getOrderFromLocalStorage -- Failed to get existing order', ex);
            return null;
        }
    }

    async setPaymentMethod(method: 'PayMongo' | 'Cash'): Promise<void> {
        this.paymentMethod.set(method);
        this._routingPhase.set('resume');
        const stored = await this.getOrderFromLocalStorage();
        if (!stored) return;
        stored.paymentMethod = method;
        await this._storage.set(constants.storageKeys.ExistingOrderDetails, stored);
    }

    markAsAccepted(): void {
        if (this._routingPhase() === 'inFlight') return;
        this._routingPhase.set('inFlight');
        this._messageBus.onOrderAccepted.next(true);
    }

    async clearStoredOrder(): Promise<void> {
        this.order.set(undefined);
        this.computation.set(undefined);
        this.paymentMethod.set(undefined);
        this._routingPhase.set('none');
        await this._storage.remove(constants.storageKeys.ExistingOrderDetails);
    }

    private async _initializeRehydration(): Promise<void> {
        try {
            // Runs before AppComponent.ngOnInit, which is where Storage.create()
            // used to happen. Without this call, the read below races an
            // uninitialized backend and silently returns no order — bypassing
            // the in-flight lock.
            await this._storage.create();
            const stored = await this.getOrderFromLocalStorage();
            if (!stored?.paymentMethod) {
                this._logger.debug('OrderService -- no in-flight order to rehydrate', stored);
                return;
            }

            this._logger.debug('OrderService -- found in-flight order in local storage', stored);
            this.order.set(stored.order);
            this.computation.set(stored.computation);
            this.paymentMethod.set(stored.paymentMethod);

            await this._valuesService.loadOrderStatusGroupsAsync();
            const groups = this._valuesService.orderStatusGroups();
            if (!groups) {
                this._logger.debug('OrderService -- status groups unavailable; deferring categorization');
                return;
            }

            const result = await this.getOrderDetails(this.isFetchingOrderDetail, stored.order.id, stored.order.temporaryAccessKey);
            if (result.type === 'error') {
                // Network blip ≠ abandoned order. Stay on 'resume' so the live
                // loop on the routing page can reconcile; 'stale' would clear
                // storage and let the user double-order.
                this._logger.debug('OrderService -- refetch failed during rehydration; defaulting to resume', result);
                this._routingPhase.set('resume');
                return;
            }

            const fresh = result.data;
            const isTerminalPrep = groups.terminalPreparation.includes(fresh.preparationStatus);
            const isPostAcceptance = groups.postAcceptancePreparation.includes(fresh.preparationStatus);

            if (isTerminalPrep || fresh.paymentStatus === 'Failed') {
                this._routingPhase.set('terminal');
            } else if (isPostAcceptance) {
                this.markAsAccepted();
            } else if (fresh.paymentStatus === 'Unset') {
                this._routingPhase.set('stale');
            } else {
                this._routingPhase.set('resume');
            }
        } finally {
            this._rehydrationResolve();
        }
    }

    async computeOrder(orderId: number): Promise<ComputeOrderResponse | undefined> {
        const response = await this._req.get<ComputeOrderResponse>(
            this._baseUrl + 'Compute/' + orderId,
            this.isComputingOrder,
        );
        if (response.type === 'error') return undefined;
        const computation = response.data;
        this._logger.debug('OrderService -- Order computed', computation);
        this.computation.set(computation);
        return computation;
    }
}

export interface OrderDetailsForStorage {
    order: CreateOrderResponse,
    computation?: ComputeOrderResponse | undefined,
    hash: string,
    paymentMethod?: 'PayMongo' | 'Cash',
}


